import React, { useEffect, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  RefreshControl,
  Alert,
} from 'react-native';
import { useTheme } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../../store';

// Smart Dashboard imports
import {
  fetchDashboardInsights,
  fetchDashboardRecommendations,
  refreshDashboard,
  selectDashboardInsights,
  selectDashboardRecommendations,
  selectDashboardLoading,
  selectDashboardError,
  selectIsRefreshing,
} from '../../store/slices/smartDashboardSlice';

// Personalization imports
import {
  updateUserPreferences,
  assignABTestVariant,
  trackInteraction,
  selectCurrentLayout,
  selectVisibleSections,
  selectABTestVariant,
  selectUserPreferences,
  selectInteractionHistory,
  updatePerformanceMetrics,
} from '../../store/slices/dashboardPersonalizationSlice';

// Component imports
import { AIGreetingHeader } from './AIGreetingHeader';
import { TodaysFocusCard } from './TodaysFocusCard';
import { ProgressCelebration } from './ProgressCelebration';
import { AdaptiveQuickActions } from './AdaptiveQuickActions';
import { AIRecommendationCard } from './AIRecommendationCard';
import { ContextualTipsProvider } from './ContextualTipsProvider';
import { LazyDashboardSection } from './LazyDashboardSection';

// Utilities
import {
  createContentPrioritizationEngine,
  createPrioritizationContext,
} from '../../utils/contentPrioritization';
import { createABTestingManager } from '../../utils/abTesting';
import { logger } from '../../utils/logger';
import {
  generateMockInsights,
  generateMockRecommendations,
} from '../../utils/mockDashboardData';

interface PersonalizedSmartDashboardProps {
  userId: string;
  onNavigate: (screen: string, params?: any) => void;
  skillLevel?: string;
  journeyStage?: string;
}

export function PersonalizedSmartDashboard({
  userId,
  onNavigate,
  skillLevel = 'beginner',
  journeyStage = 'onboarding',
}: PersonalizedSmartDashboardProps) {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  // Smart Dashboard selectors
  const insights = useSelector(selectDashboardInsights);
  const recommendations = useSelector(selectDashboardRecommendations);
  const loading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);
  const isRefreshing = useSelector(selectIsRefreshing);

  // Personalization selectors
  const currentLayout = useSelector(selectCurrentLayout);
  const visibleSections = useSelector(selectVisibleSections);
  const abTestVariant = useSelector(selectABTestVariant);
  const userPreferences = useSelector(selectUserPreferences);
  const interactionHistory = useSelector(selectInteractionHistory);

  // Initialize personalization engines
  const prioritizationEngine = useMemo(() => {
    return createContentPrioritizationEngine(
      userPreferences?.contentPreferences
        ? {
            weights: {
              aiConfidence: 0.3,
              userEngagement: 0.3,
              recency: 0.15,
              personalRelevance: 0.15,
              socialProof: 0.1,
            },
          }
        : undefined
    );
  }, [userPreferences]);

  const abTestingManager = useMemo(() => {
    return createABTestingManager();
  }, []);

  // Initialize dashboard data and personalization
  useEffect(() => {
    if (userId) {
      // For testing, create mock user preferences if none exist
      if (!userPreferences) {
        const mockPreferences = {
          userId,
          layoutId: currentLayout?.id || 'default',
          sectionPreferences: {},
          interactionPatterns: [],
          contentPreferences: [],
          accessibilitySettings: {
            highContrast: false,
            largeText: false,
            reduceMotion: false,
            screenReader: false,
            voiceNavigation: false,
            simplifiedExplanations: false,
          },
          performanceSettings: {
            lazyLoadingEnabled: true,
            imageOptimization: true,
            animationsEnabled: true,
            prefetchingEnabled: true,
            cacheSize: 50,
            backgroundSync: true,
          },
          lastUpdated: new Date().toISOString(),
        };
        dispatch(updateUserPreferences(mockPreferences));
      }

      // Assign A/B test variant (this will fail gracefully in test environment)
      dispatch(assignABTestVariant(userId));

      // Fetch dashboard data if not already loaded
      if (!insights) {
        dispatch(fetchDashboardInsights(userId));
      }
      if (recommendations.length === 0) {
        dispatch(fetchDashboardRecommendations({ userId, limit: 10 }));
      }
    }
  }, [
    dispatch,
    userId,
    insights,
    recommendations.length,
    userPreferences,
    currentLayout,
  ]);

  // Get recommendations (use mock data if API data is not available)
  const effectiveRecommendations = useMemo(() => {
    if (recommendations.length > 0) {
      return recommendations;
    }

    // Use mock data for testing - convert to ContentRecommendation format
    const mockRecs = generateMockRecommendations(skillLevel, journeyStage, 10);
    return mockRecs.map(rec => ({
      id: rec.id,
      userId: rec.userId,
      contentId: rec.contentId,
      contentType:
        rec.contentType === 'lesson' ? 'practice' : (rec.contentType as any),
      title: rec.title,
      description: rec.description,
      thumbnailUrl: rec.thumbnailUrl || '',
      estimatedMinutes: rec.estimatedMinutes,
      difficultyLevel:
        rec.difficultyLevel === 'beginner'
          ? 0.2
          : rec.difficultyLevel === 'intermediate'
            ? 0.5
            : rec.difficultyLevel === 'advanced'
              ? 0.8
              : 0.9,
      skillAlignment: rec.reasoning.skillAlignment,
      confidence: rec.confidence,
      reasoning: rec.reasoning,
      priority: rec.priorityScore || 0.5,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date().toISOString(),
      metadata: rec.metadata,
    }));
  }, [recommendations, skillLevel, journeyStage]);

  // Get insights (use mock data if API data is not available)
  const effectiveInsights = useMemo(() => {
    if (insights) {
      return insights;
    }

    // Use mock data for testing - cast to DashboardInsights type
    return generateMockInsights(userId, skillLevel, journeyStage) as any;
  }, [insights, userId, skillLevel, journeyStage]);

  // Prioritize recommendations based on user preferences and context
  const prioritizedRecommendations = useMemo(() => {
    if (effectiveRecommendations.length === 0) return [];

    const context = createPrioritizationContext(
      userPreferences || undefined,
      interactionHistory,
      effectiveInsights,
      skillLevel,
      journeyStage,
      [] // Recent activity - would be populated from user state
    );

    const prioritized = prioritizationEngine.prioritizeContent(
      effectiveRecommendations,
      context
    );
    return prioritized.map(p => ({
      ...effectiveRecommendations.find((r: any) => r.id === p.id)!,
      priorityScore: p.finalScore,
      priorityReasons: p.reasons,
    }));
  }, [
    effectiveRecommendations,
    userPreferences,
    interactionHistory,
    effectiveInsights,
    skillLevel,
    journeyStage,
    prioritizationEngine,
  ]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    if (userId) {
      const startTime = performance.now();
      dispatch(refreshDashboard(userId)).finally(() => {
        const loadTime = performance.now() - startTime;
        dispatch(
          updatePerformanceMetrics({
            metric: 'dashboard_refresh',
            value: loadTime,
            category: 'loadTime',
          })
        );
      });
    }
  }, [dispatch, userId]);

  // Handle errors
  useEffect(() => {
    if (error && error.recoverable) {
      Alert.alert('Dashboard Update', error.message, [
        { text: 'Retry', onPress: handleRefresh },
        { text: 'OK', style: 'cancel' },
      ]);
    }
  }, [error, handleRefresh]);

  // Track interactions and update personalization
  const handleInteraction = useCallback(
    (sectionId: string, actionType: string, metadata?: any) => {
      const startTime = performance.now();

      // Track interaction for personalization
      dispatch(trackInteraction({ userId, sectionId, actionType, metadata }));

      // Track A/B test event
      if (abTestVariant) {
        abTestingManager.trackEvent({
          userId,
          testId: abTestVariant.id,
          variantId: abTestVariant.id,
          eventType: actionType,
          eventData: metadata,
        });
      }

      // Track interaction latency
      const latency = performance.now() - startTime;
      dispatch(
        updatePerformanceMetrics({
          metric: sectionId,
          value: latency,
          category: 'interaction',
        })
      );
    },
    [dispatch, userId, abTestVariant, abTestingManager]
  );

  // Handle navigation actions with tracking
  const handleActionPress = useCallback(
    (actionType: string, actionId?: string, sectionId?: string) => {
      // Track interaction
      if (sectionId) {
        handleInteraction(sectionId, actionType, { actionId });
      }

      // Navigate
      switch (actionType) {
        case 'continue_journey':
          onNavigate('Journey');
          break;
        case 'try_recipe':
          if (actionId) {
            onNavigate('RecipeDetail', { recipeId: actionId });
          } else {
            onNavigate('Recipes');
          }
          break;
        case 'browse_recipes':
          onNavigate('Recipes');
          break;
        case 'browse_videos':
          onNavigate('Videos');
          break;
        case 'browse_social':
          onNavigate('Community');
          break;
        case 'watch_video':
          if (actionId) {
            onNavigate('VideoDetail', { videoId: actionId });
          } else {
            onNavigate('Videos');
          }
          break;
        case 'take_quiz':
          if (actionId) {
            onNavigate('Quiz', { quizId: actionId });
          } else {
            onNavigate('Quizzes');
          }
          break;
        case 'practice_skill':
          if (actionId) {
            onNavigate('SkillPractice', { skillId: actionId });
          } else {
            onNavigate('Skills');
          }
          break;
        case 'view_progress':
          onNavigate('Progress');
          break;
        case 'view_achievements':
          onNavigate('Achievements');
          break;
        case 'view_favorites':
          onNavigate('Favorites');
          break;
        case 'search':
          onNavigate('Search');
          break;
        case 'try_recommendation':
          if (actionId) {
            onNavigate('RecipeDetail', { recipeId: actionId });
          } else {
            onNavigate('Recipes');
          }
          break;
        default:
          logger.warn('Unknown action type:', actionType);
      }
    },
    [handleInteraction, onNavigate]
  );

  // Handle tip actions
  const handleTipAction = useCallback(
    (tipId: string, action: string) => {
      handleInteraction('tips', action, { tipId });

      switch (action) {
        case 'start_tutorial':
          onNavigate('Tutorial');
          break;
        case 'try_feature':
          // Navigate to specific feature
          break;
        case 'apply_suggestion':
          // Apply the suggestion
          break;
        case 'fix_issue':
          // Navigate to troubleshooting
          break;
        case 'learn_more':
          onNavigate('Help', { tipId });
          break;
      }
    },
    [handleInteraction, onNavigate]
  );

  // Render section based on type and loading strategy
  const renderSection = useCallback(
    (section: any) => {
      let content: React.ReactNode = null;

      switch (section.type) {
        case 'greeting':
          content = (
            <AIGreetingHeader
              greeting={effectiveInsights?.personalizedGreeting || null}
              insights={effectiveInsights}
              loading={loading.insights}
              onRefresh={handleRefresh}
            />
          );
          break;

        case 'focus':
          content = effectiveInsights?.nextBestAction ? (
            <TodaysFocusCard
              action={effectiveInsights.nextBestAction}
              onActionPress={(actionType, actionId) =>
                handleActionPress(actionType, actionId, section.id)
              }
              loading={loading.insights}
            />
          ) : null;
          break;

        case 'progress':
          content = effectiveInsights ? (
            <ProgressCelebration
              insights={effectiveInsights}
              onViewProgress={() =>
                handleActionPress('view_progress', undefined, section.id)
              }
              onViewAchievements={() =>
                handleActionPress('view_achievements', undefined, section.id)
              }
            />
          ) : null;
          break;

        case 'quick_actions':
          content = (
            <AdaptiveQuickActions
              insights={effectiveInsights}
              recommendations={prioritizedRecommendations}
              onActionPress={(actionType, actionId) =>
                handleActionPress(actionType, actionId, section.id)
              }
              loading={loading.recommendations}
            />
          );
          break;

        case 'recommendations':
          const maxRecommendations = section.customization.maxItems || 3;
          const displayRecommendations = prioritizedRecommendations.slice(
            0,
            maxRecommendations
          );

          content =
            displayRecommendations.length > 0 ? (
              <View style={styles.recommendationsSection}>
                {displayRecommendations.map(recommendation => (
                  <AIRecommendationCard
                    key={recommendation.id}
                    recommendation={recommendation}
                    onAccept={() =>
                      handleActionPress(
                        'try_recipe',
                        recommendation.contentId,
                        section.id
                      )
                    }
                    onDismiss={() =>
                      handleInteraction(section.id, 'dismiss_recommendation', {
                        recommendationId: recommendation.id,
                      })
                    }
                    onFeedback={feedback =>
                      handleInteraction(section.id, 'recommendation_feedback', {
                        recommendationId: recommendation.id,
                        feedback,
                      })
                    }
                  />
                ))}
              </View>
            ) : null;
          break;

        case 'tips':
          content = (
            <ContextualTipsProvider
              userId={userId}
              journeyStage={journeyStage}
              skillLevel={skillLevel}
              currentScreen='dashboard'
              maxTips={section.customization.maxItems || 2}
              onTipAction={handleTipAction}
            />
          );
          break;

        default:
          content = null;
      }

      if (!content) return null;

      // Wrap in lazy loading component if needed
      if (section.loadingStrategy !== 'eager') {
        return (
          <LazyDashboardSection
            key={section.id}
            section={section}
            onLoad={() => handleInteraction(section.id, 'section_loaded')}
            onError={error =>
              logger.warn(`Failed to load section ${section.id}:`, error)
            }
          >
            {content}
          </LazyDashboardSection>
        );
      }

      return (
        <View key={section.id} style={styles.section}>
          {content}
        </View>
      );
    },
    [
      effectiveInsights,
      prioritizedRecommendations,
      loading,
      handleRefresh,
      handleActionPress,
      handleInteraction,
      handleTipAction,
      userId,
      journeyStage,
      skillLevel,
    ]
  );

  if (!currentLayout) {
    return null; // Loading state handled by parent
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            colors={[theme.colors.primary]}
            tintColor={theme.colors.primary}
          />
        }
      >
        {/* Render sections based on current layout */}
        {visibleSections.map(renderSection)}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    marginVertical: 4,
  },
  recommendationsSection: {
    paddingHorizontal: 16,
    paddingBottom: 8,
    gap: 12,
  },
});
